import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import MDNotice from 'md-noticejs';
import MDZendeskjs from 'md-zendeskjs';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ErrorPage as ErrorPageComponent } from '@/components/Elements/ErrorPage';
import { PrivateFooter } from '@/components/Footer';
import { PrivateHeader } from '@/components/Header';
import {
  createPaymentReminderNotification,
  dismissNewFreeNotification,
  dismissNoBillingReminderNotification,
  dismissPaymentReminderNotification,
  showNotificationNewFree,
  showNotificationNoBilling,
} from '@/components/Notifications/NotificationHelpers/helpers';
import { PrivateSidebar } from '@/components/Sidebar';
import {
  APP_BACKEND_LOGOUT_URL,
  APP_HOME_URL,
  APP_SIGNUP_URL,
  DEFAULT_THEME,
  SIGNUP_BACKEND_URL,
} from '@/config';
import {
  EUR,
  INTERVAL_COUNT_FOR_BILLING_MONTH,
  REQUEST_SUCCESS,
  ROL_ADM_MDIRECTOR,
  ROL_ADM_MDIRECTOR_NEWFREE,
} from '@/config/appConstants';
import { getMeInfo } from '@/features/me';
import { getUserProducts, ResponseError, useDeleteUserPendingProduct } from '@/features/myProducts';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import { getAunoaInitData, loadAunoa } from '@/lib/Aunoa/aunoa';
import { useAuth } from '@/lib/auth';
import { getAllAxiosData, GetterAxios } from '@/lib/axios';
import { buildGTMNavigationData, sendEventGtm } from '@/lib/gtm/gtmUtils';
import {
  BillingMode,
  Currency,
  iAuthInfo,
  IntervalBillingMode,
  ProductTypes,
  responseStatus,
  UserProductTypes,
} from '@/types';
import {
  filterUserProductsByPending,
  getUserData,
  isBillingMode,
  buildProductsCheckingInStorageForPendings,
  isAppSumo,
  getAppUrlFromRol,
} from '@/utils/helpers';
import storage from '@/utils/storage';

import { ErrorModal } from '../Elements/ErrorModal';

type PrivateLayoutProps = {
  children: React.ReactNode;
  hiddenHeader: boolean;
  hiddenFooter: boolean;
  hiddenNotification: boolean;
};

export const PrivateLayout = ({
  children,
  hiddenHeader,
  hiddenFooter,
  hiddenNotification,
}: PrivateLayoutProps) => {
  const { t, i18n } = useTranslation();
  const handleError = useErrorHandler();
  const sendDataToGTM = useGTMDispatch();
  const { billingMode } = useParams();
  const { setMe, getMe, isMeSet } = useMe();
  const { initBillingDetails, getBillingDetails, isBillingDetailsSet } = useGroupBillingDetails();

  const {
    setSelectedProduct,
    setSelectedProductCurrency,
    getSelectedUserProduct,
    setSelectedProductBillingMode,
    isSelectedProductCurrencySet,
  } = useUserProducts();

  const [isLogged, setIsLogged] = useState(false);
  const [isErrorLogged, setIsErrorLogged] = useState(false);
  const [userProducts, setUserProducts] = useState<UserProductTypes[] | null>(null);
  const [statusDeleteUserPendingProductError, setStatusDeleteUserPendingProductError] = useState({
    status: 0,
    detail: '',
  });

  const { login, getAuthInfo, removeItemInLocalStorage } = useAuth();

  const authInfo: iAuthInfo = getAuthInfo();
  const [, setCompanyId] = useState<string | null>(null);

  const deleteUserPendingProductMutation = useDeleteUserPendingProduct();

  useEffect(() => {
    if (!isLogged) {
      login((status: responseStatus) => {
        if (status === REQUEST_SUCCESS) {
          setIsLogged(true);
          if (
            authInfo.getRolId() != ROL_ADM_MDIRECTOR_NEWFREE &&
            authInfo.getRolId() != ROL_ADM_MDIRECTOR
          ) {
            window.location.assign(getAppUrlFromRol(authInfo.getRolId()));
          }
        } else {
          setIsErrorLogged(true);
        }
      });
    }
  });

  useEffect(() => {
    const setProducts = (userProducts: UserProductTypes[]) => {
      userProducts = buildProductsCheckingInStorageForPendings(
        userProducts,
        storage
      ) as UserProductTypes[];
      userProducts && setUserProducts(userProducts);
      const userProduct = filterUserProductsByPending(userProducts),
        product = userProduct?.product;

      userProducts &&
        product &&
        setSelectedProduct(
          product,
          (product?.prices[0].recurring?.interval_count as IntervalBillingMode) ||
            INTERVAL_COUNT_FOR_BILLING_MONTH
        );

      userProducts &&
        product &&
        setSelectedProductCurrency((product?.prices[0].currency as Currency) || EUR);

      billingMode &&
        isBillingMode(billingMode as BillingMode) &&
        setSelectedProductBillingMode(billingMode as BillingMode);
    };

    if (isLogged) {
      const groupId = authInfo.getGroupId();
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId } },
        { callback: getMeInfo },
      ];
      const setters: any = [setProducts, setMe];
      const pageData = getAllAxiosData(getters, setters);

      initBillingDetails(groupId);

      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [
    isLogged,
    authInfo,
    handleError,
    initBillingDetails,
    setSelectedProduct,
    setSelectedProductCurrency,
    setSelectedProductBillingMode,
    billingMode,
    setMe,
  ]);

  useEffect(() => {
    if (isLogged && isMeSet() && isBillingDetailsSet()) {
      loadAunoa(getAunoaInitData(getMe(), getBillingDetails()));

      const dismissAllNotifications = () => {
        dismissNoBillingReminderNotification();
        dismissNewFreeNotification();
      };

      if (!isSelectedProductCurrencySet()) setSelectedProductCurrency(getMe().userInfo.currency);

      const onReminderCancelButton = async (product: ProductTypes | null | undefined) => {
        if (!product) return;
        storage.clearUserData();
        if (!isAppSumo(getMe())) {
          await deleteUserPendingProductMutation.mutateAsync({
            mdirectorId: product.mdirectorId,
            onErrorCallback: onDeleteUserPendingProductErrorCallback,
            onSuccessCallback: () => {
              window.location.reload();
            },
          });
        }
      };
      const onDeleteUserPendingProductErrorCallback = (
        { status, detail }: ResponseError,
        mdirectorId: string
      ) => {
        dismissAllNotifications();
        dismissPaymentReminderNotification(mdirectorId);
        setStatusDeleteUserPendingProductError({ status, detail });
      };
      const onReminderActionButton = (
        product: ProductTypes,
        currency: Currency,
        intervalBillingMode: IntervalBillingMode | null
      ) => {
        product && currency && intervalBillingMode && {}; // Unused variables. Making linter happy by using them.
        window.location.assign('/account/my-rate/checkout');
      };

      if (!hiddenNotification && statusDeleteUserPendingProductError.status == 0) {
        dismissAllNotifications();
        showNotificationNoBilling(getBillingDetails(), getMe());
        showNotificationNewFree(getBillingDetails(), getMe());
        const userProduct = userProducts && filterUserProductsByPending(userProducts);
        const { selectedProductIntervalBillingMode, selectedProduct, selectedProductCurrency } =
          getSelectedUserProduct();

        selectedProduct &&
          userProduct &&
          createPaymentReminderNotification(
            selectedProduct,
            selectedProductCurrency,
            selectedProductIntervalBillingMode,
            onReminderActionButton,
            onReminderCancelButton
          );
      }
    }
  }, [
    getBillingDetails,
    isBillingDetailsSet,
    isLogged,
    isMeSet,
    t,
    hiddenNotification,
    getSelectedUserProduct,
    userProducts,
    deleteUserPendingProductMutation,
    getMe,
    isSelectedProductCurrencySet,
    setSelectedProductCurrency,
    statusDeleteUserPendingProductError,
  ]);

  useEffect(() => {
    if (isLogged && isMeSet() && isBillingDetailsSet() && userProducts) {
      sendEventGtm(
        buildGTMNavigationData(getUserData(getMe(), i18n), getBillingDetails(), userProducts),
        sendDataToGTM
      );
    }
  }, [
    isLogged,
    getMe,
    isMeSet,
    getBillingDetails,
    isBillingDetailsSet,
    userProducts,
    i18n,
    sendDataToGTM,
  ]);

  useEffect(() => {
    const getNotice = () => {
      isLogged &&
        MDNotice.init({
          el: '#noticeContainer',
          url: SIGNUP_BACKEND_URL + 'notice/payments',
          cors: true,
          userData: {
            user: authInfo.getUserName(),
            userType: authInfo.getRolId(),
            product: 'payments',
            name: authInfo.getUserName(),
            lang: i18n.language,
          },
        });
    };

    const observer = new MutationObserver(() => {
      const noticeContainer = document.getElementById('noticeContainer');
      if (noticeContainer) {
        getNotice();
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }, [isLogged, authInfo, i18n]);

  if (isLogged && isMeSet() && isBillingDetailsSet()) {
    const onChangeLanguage = (lang: string) => {
      void i18n.changeLanguage(lang);
    };

    const openSupportChat = () => {
      if (!window.mdZendeskjs) {
        window.mdZendeskjs = new MDZendeskjs({
          lang: i18n.language,
          classError: 'md__header__error',
        });
        const onLoadSucess = () => {
          window.mdZendeskjs.openPopup();
        };
        const onLoadFail = () => {};
        window.mdZendeskjs.init(onLoadSucess, onLoadFail);
      } else {
        window.mdZendeskjs.openPopup();
      }
    };

    const onChangeCompany = (companyId: string) => {
      authInfo.setCompanyData(companyId);
      setCompanyId(companyId);
    };

    const appClass = 'outer-wrapper';

    document.body.classList.add('payments', DEFAULT_THEME);

    const renderStatusPaymentErrorModal = () => (
      <ErrorModal
        status={statusDeleteUserPendingProductError.status}
        detail={statusDeleteUserPendingProductError.detail}
        onRequestClose={() => {
          setStatusDeleteUserPendingProductError({ status: 0, detail: '' });
        }}
      />
    );

    return (
      <div className={`mdirector__main__container ${appClass}`}>
        <div id="md__sidebar__wrapper">
          <PrivateSidebar />
        </div>
        <main>
          <div id="header">
            {!hiddenHeader && (
              <PrivateHeader
                authInfo={authInfo}
                onChangeCompany={onChangeCompany}
                lang={i18n.language}
                onChangeLanguage={onChangeLanguage}
                logoutUrl={APP_BACKEND_LOGOUT_URL}
                myAccountUrl={`${APP_SIGNUP_URL}accounts`}
                getHelpUrl={() => t('PrivateHeader.helpURL')}
                onLogout={() => removeItemInLocalStorage}
                openSupportChat={openSupportChat}
              />
            )}
          </div>
          <section>
            <div className="main__section__wrapper">
              <div id="noticeContainer"></div>
              <div className="main" id="scene">
                <div className="md__payments__main--container">
                  {children}
                  {statusDeleteUserPendingProductError.status > 0 &&
                    renderStatusPaymentErrorModal()}
                </div>
              </div>
            </div>
            <div id="footer">{!hiddenFooter && <PrivateFooter lang={i18n.language} />}</div>
          </section>
        </main>
      </div>
    );
  }

  if (isErrorLogged) {
    return <ErrorPageComponent actionButton={APP_HOME_URL} />;
  }

  return <></>;
};
